/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import { centerClient } from '../../shared/clients';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, RowClassParams, RowDoubleClickedEvent } from 'ag-grid-community';
import context, { localStorageKeys } from './context/shared';
import { BlickAgGrid, AG_GRID_LOCALE_RU } from 'blick.react.aggrid';
import { findClientNodeByName } from '../../utils/treeFunctions';

interface IClientsTableItem {
    number: string;
    name: string
    loginsCount: number;
    devicesCount: number;
}

const getDefaultColumnWidth = (field: keyof (IClientsTableItem)) => {
    switch (field) {
        case 'name':
            return 400;
        case 'loginsCount':
            return 200;
        case 'devicesCount':
            return 200;
        default:
            return 150;
    }
}

const ClientsTable = () => {
    const [data, setData] = useState<IClientsTableItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const getStoredColumnWidth = (column: keyof (IClientsTableItem)) => {
        const widthsStorage = getWidthStorage();
        if (widthsStorage) {
            const columnWidth = widthsStorage[column];
            if (columnWidth)
                return columnWidth;
        }
        return getDefaultColumnWidth(column);
    }

    function getWidthStorage() {
        return JSON.parse(localStorage.getItem(localStorageKeys.clientsTableWidths) ?? "null");
    }
    const resizeHandle = (state) => {
        if (!state.column)
            return;

        const colId = state.column.colId;
        const colWidth = state.column.actualWidth;
        let widthsStorage = getWidthStorage();
        if (!widthsStorage)
            widthsStorage = {};

        widthsStorage[colId] = colWidth;

        localStorage.setItem(localStorageKeys.clientsTableWidths, JSON.stringify(widthsStorage));
    }

    const totalLogins = useMemo(() => {
        return data.reduce((sum, row) =>
            sum + row.loginsCount, 0);
    }, [data]);
    const totalDevices = useMemo(() => {        
        return data.reduce((sum, row) => sum + row.devicesCount, 0);
    }, [data]);

    const columns = useMemo<ColDef<IClientsTableItem>[]>(
        () => [
            {
                headerName: "№",
                width: 50,
                maxWidth: 50,
                editable: false,                
                valueGetter: (props) => {
                    const row = props.node;
                    if (!row?.isRowPinned())
                        return row!.rowIndex! + 1;
                    else
                        return "";
                }
            },
            {
                field: 'name',
                headerName: "Название",
                width: getStoredColumnWidth('name'),
                editable: false
            },
            {
                field: 'loginsCount',
                headerName: "Число логинов",
                width: getStoredColumnWidth('loginsCount'),
                editable: false,
                valueGetter: (props) => {
                    return props.data?.loginsCount;
                }
            },
            {
                field:'devicesCount' ,
                headerName: "Число устройств",
                width: getStoredColumnWidth('devicesCount'),
                editable: false,
                valueGetter: (props) => {
                    return props.data?.devicesCount;
                }
               
            }
        ],
        []
    )
    const pinnedBottomRowData = useMemo<any[]>(() => {
        return [
            {
                rowNumber:"",
                loginsCount: `Итого логинов: ${totalLogins}`,
                devicesCount: `Итого устройств: ${totalDevices}`,            
            },
        ];
    }, [totalLogins, totalDevices]);

    //const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            loadClients();
            //setIsLoading(false);
        }
    }, []);

    function loadClients() {
        setIsLoading(true);
        centerClient.getClientsTable()
            .then((res) => {
                const data = res.data;
                if (data)
                    setData(data as unknown as IClientsTableItem[]);
            })
            .catch(() => void 0)
            .finally(() => setIsLoading(false));
    }
    const getRowStyle = (row: RowClassParams) => {
        const pinned = row.node.isRowPinned();
        const color = pinned ? 'var(--bg-lighter)' : 'var(--bg)';
        const fontWeight = pinned ? 700 : 400;
        const style = {
            backgroundColor: color,
            fontWeight: fontWeight
        }
        return style;
    }
    const tableRef = useRef<AgGridReact>(null);

    const { setCurrentNode, clientsTreeData, setPendingScrollNodeKey } = context;

    const onRowDoubleClicked = (e: RowDoubleClickedEvent<IClientsTableItem>) => {
        if (e.node?.isRowPinned() || !e.data?.name)
            return;
        const node = findClientNodeByName(e.data.name, clientsTreeData);
        if (!node)
            return;
        setCurrentNode(node);
        setPendingScrollNodeKey(node.key);
    };

    return (
        <BlickAgGrid
            showRestoreColumnsButton={true}
            loading={isLoading}
            rowData={data}
            ref={tableRef}
            //theme={myAgReactTheme}
            localeText={AG_GRID_LOCALE_RU}
            columnDefs={columns}
            onColumnResized={resizeHandle}
            onRowDoubleClicked={onRowDoubleClicked}
            getRowStyle={getRowStyle }
            onModelUpdated={(e) => { e.api.refreshCells() }}
            pinnedBottomRowData={pinnedBottomRowData}
            />
    )
;
};

export default ClientsTable;
